import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useTokenStore } from './token';
import { Job, JobAggregates } from '../../jobs-for-queue.js';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';

TimeAgo.addDefaultLocale(en);
const timeAgo = new TimeAgo('en-US');

export type JobsResponse = {
  aggregates: JobAggregates;
  jobs: Job[];
  paging: {
    limit: number;
    offset: number;
    total: number;
  };
};
export const useJobStore = defineStore('jobs', () => {
  const { csToken } = storeToRefs(useTokenStore());

  const currentQueue = ref('');
  const currentStatus = ref('');
  const currentOffset = ref(0);
  const currentPageSize = ref(100);
  const jobs = ref([] as Job[]);
  const aggregates = ref({ jobs: {}, shops: {} } as JobAggregates);
  const isLoading = ref(false);
  const isLoaded = ref(false);
  const showCurrentQueue = ref(false);
  const aggregateJobTotal = computed(() => {
    return Object.values(aggregates.value.jobs).reduce((acc, count) => acc + count, 0);
  });

  const aggregateJobSorted = computed(() => {
    return Object.entries(aggregates.value.jobs)
      .sort((a, b) => b[1] - a[1])
      .reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as JobAggregates['jobs'],
      );
  });

  const aggregateShopTotal = computed(() => {
    return Object.values(aggregates.value.shops).reduce((acc, count) => acc + count, 0);
  });

  const aggregateShopSorted = computed(() => {
    return Object.entries(aggregates.value.shops)
      .sort((a, b) => b[1] - a[1])
      .reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as JobAggregates['shops'],
      );
  });

  async function loadJobs(queue: string, status: string, limit?: number = 100, offset?: number = 0) {
    if (csToken.value === '' || isLoading.value) {
      return;
    }
    if (queue.startsWith('jobs-')) {
      queue = 'versioned';
    }
    isLoading.value = true;
    currentQueue.value = queue;
    currentStatus.value = status;
    const query = new URLSearchParams({ queue, status, limit, offset }).toString();
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/jobs?${query}`, {
      headers: {
        Authorization: `Bearer ${csToken.value}`,
      },
    });
    isLoading.value = false;
    if (response.ok) {
      const data = (await response.json()) as JobsResponse;
      jobs.value = data.jobs;
      aggregates.value = data.aggregates;
      isLoaded.value = true;
      currentOffset.value = data.paging.offset;
      currentPageSize.value = data.paging.limit;
      return;
    }
    alert(`Failed to load job counts ${response.status}: ${JSON.stringify(await response.json())}`);
  }

  function formattedDate(timestamp: number) {
    return timeAgo.format(new Date(timestamp * 1000));
  }

  function resetJobs() {
    jobs.value = [];
    aggregates.value = { jobs: {}, shops: {} };
    isLoaded.value = false;
  }

  return {
    currentQueue,
    currentStatus,
    currentOffset,
    currentPageSize,
    jobs,
    aggregates,
    isLoading,
    isLoaded,
    showCurrentQueue,
    aggregateJobTotal,
    aggregateJobSorted,
    aggregateShopTotal,
    aggregateShopSorted,
    formattedDate,
    loadJobs,
    resetJobs,
  };
});
